// "Use this tablet as a wall panel."
//
// Turns THIS device into a kiosk: it opens straight onto a location's button
// sheet and needs the account password to leave.
//
// ⚠️ THE SETTING IS LOCAL TO THIS TABLET (core/wall/kioskMode). A wall tablet
// and the owner's phone are usually the same login, so storing it on the
// account would turn the phone into a kiosk too. The dialog says so, because
// "this tablet" is otherwise easy to read as "my account".

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, MenuItem,
  TextField, Typography, Alert, Switch, FormControlLabel, Box,
} from '@mui/material';
import { useApp } from '../../core/state/AppContext';
import { loadKiosk, setKiosk } from '../../core/wall/kioskMode';
import { parseLocationPanel, panelsForLocation } from '../../core/wall/wallLocation';
import { fetchPanels } from '../desktop/pages/wall/wallApi';

export default function KioskSetupDialog({ open, onClose }) {
  // The tablet is pinned to the location that is CURRENTLY SELECTED — there is
  // no second location picker here. Choosing the site is what the header does,
  // and asking twice invites the two to disagree.
  const { userId, locationId, selectedLocation } = useApp();
  const [enabled, setEnabled] = useState(false);
  const [panelId, setPanelId] = useState('');
  const [panels, setPanels] = useState([]);

  useEffect(() => {
    if (!open) return;
    const k = loadKiosk();
    setEnabled(k.enabled);
    setPanelId(k.panelId ?? '');
    fetchPanels(userId).then(setPanels).catch(() => setPanels([]));
  }, [open, userId]);

  const forLocation = locationId == null ? [] : panelsForLocation(panels, locationId);

  const save = () => {
    setKiosk(enabled, enabled ? locationId : null, enabled && panelId !== '' ? panelId : null);
    onClose(true);
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>Wall panel mode</DialogTitle>
      <DialogContent>
        <FormControlLabel
          control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
          label="Use this tablet as a wall panel"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Applies to THIS device only — your phone signed into the same account
          is unaffected. The app will open on the panel and ask for your
          password to leave it.
        </Typography>

        {enabled && (
          <Box>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {'Location: '}
              <b>{selectedLocation?.LocationName || `#${locationId}`}</b>
            </Typography>

            <TextField
              select size="small" fullWidth label="Panel" value={panelId}
              onChange={(e) => setPanelId(e.target.value)}
              disabled={locationId === ''}
              helperText={locationId !== '' && forLocation.length === 0
                ? 'No wall panels for this location yet — design one first.'
                : 'Leave blank to open the first panel.'}
            >
              <MenuItem value="">First panel</MenuItem>
              {forLocation.map((p) => (
                <MenuItem key={p.WallPanelId} value={p.WallPanelId}>
                  {parseLocationPanel(p.PanelName)?.name || p.PanelName}
                </MenuItem>
              ))}
            </TextField>

            <Alert severity="info" sx={{ mt: 1.5, py: 0 }}>
              To turn this off later, exit the panel with your password and come
              back to this screen.
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button variant="contained" onClick={save}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
